/*
 * <twc-brand-plans> — the brand landing page's membership tiles.
 *
 * Each tile IS the plan choice, so a tile click must not detour through the
 * sign-up page: it takes the visitor straight to checkout with that plan, using
 * exactly the sequence assets/twc-signup.js established on /pages/signup (that
 * file is the reference implementation; this one applies it to a two-tile,
 * no-toggle layout).
 *
 * Children (rendered by sections/twc-membership-tiles.liquid +
 * blocks/twc-brand-plan-tile.liquid):
 *   [data-twc-plan]     the tile links; carry data-variant-id +
 *                       data-selling-plan-id (numeric IDs from theme settings —
 *                       Shopify has no selling-plan picker, the accepted ID
 *                       exception, same as the sign-up tiles)
 *   [data-twc-message]  status/error line (role="status")
 *
 * Add-to-cart truth (see assets/twc-signup.js header): cart
 * permalinks DROP selling plans, so the Ajax Cart API is the only working path.
 * /cart/add.js takes only an `items` array (per-item `selling_plan`), while
 * cart-level `attributes` ride a /cart/update.js of their own.
 *
 * Clear-first invariant: this flow always starts from an empty
 * cart, so a member can never check out a mixed cart from a join entry point.
 * The sequence is TwcCartAttributes.clearThenAdd()
 * (assets/twc-cart-attributes.js):
 *   1. POST /cart/clear.js
 *   2. POST /cart/update.js {
 *        attributes: {
 *          twc_referred_by: code,        // only when captured
 *          date_of_birth: 'YYYY-MM-DD',  // '' when this page knows none
 *          purchase_type: ''             // '' REMOVES a gift marker
 *        }
 *      }
 *      The clear removes the LINE ITEMS only and keeps the cart attributes,
 *      so this write is what clears a gift marker an earlier gifting checkout
 *      left behind.
 *   3. POST /cart/add.js { items: [{ id, quantity: 1, selling_plan }] }
 *   4. window.location.href = '/checkout'
 * FATAL at every step — a failure aborts with an inline message and NO
 * redirect (never a partial cart).
 *
 * Progressive enhancement: the tiles are real links to /pages/signup. Without
 * JavaScript — or when a tile has no variant wired up (theme editor, staging) —
 * the click is left alone and the visitor lands on the sign-up page, which is
 * where every join CTA without a plan selector goes anyway.
 */
(function (global) {
  'use strict';

  if (!global.customElements || global.customElements.get('twc-brand-plans')) return;

  function routeRoot() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  var Plans = function () {
    return Reflect.construct(HTMLElement, [], Plans);
  };
  Plans.prototype = Object.create(HTMLElement.prototype);
  Plans.prototype.constructor = Plans;

  Plans.prototype.connectedCallback = function () {
    var self = this;
    self._abort = new AbortController();
    var signal = self._abort.signal;

    self._tiles = Array.prototype.slice.call(self.querySelectorAll('[data-twc-plan]'));
    self._message = self.querySelector('[data-twc-message]');
    self._busyOn = false;

    // Copy comes from data-* attributes only (the section always renders them
    // from locales) — no English strings duplicated in here to drift.
    self._t = function (key) {
      var v = self.dataset[key];
      return v != null ? v : '';
    };

    self._tiles.forEach(function (tile) {
      tile.addEventListener(
        'click',
        function (event) {
          self._onTileClick(event, tile);
        },
        { signal: signal }
      );
    });

    // Back-forward cache: returning from checkout restores the frozen page —
    // release the pending state so the tiles work again.
    global.addEventListener(
      'pageshow',
      function (event) {
        if (event.persisted) self._busy(false);
      },
      { signal: signal }
    );
  };

  Plans.prototype.disconnectedCallback = function () {
    if (this._abort) this._abort.abort();
  };

  Plans.prototype._say = function (text, isError) {
    if (!this._message) return;
    this._message.textContent = text || '';
    this._message.hidden = !text;
    this._message.classList.toggle('twc-brand-plans__message--error', !!isError);
  };

  Plans.prototype._busy = function (on) {
    this._busyOn = !!on;
    this.toggleAttribute('data-twc-busy', !!on);
    this._tiles.forEach(function (t) {
      t.setAttribute('aria-busy', on ? 'true' : 'false');
    });
    if (!on) this._say('');
  };

  Plans.prototype._onTileClick = function (event, tile) {
    var self = this;

    if (self._busyOn) {
      event.preventDefault();
      return;
    }

    var variantId = parseInt(tile.dataset.variantId, 10);
    var sellingPlanId = parseInt(tile.dataset.sellingPlanId, 10);

    // No variant wired up → leave the link alone: the href is the sign-up page.
    if (!variantId || isNaN(variantId)) return;

    event.preventDefault();

    // TwcCartAttributes owns the clear-first cart write, so a tile cannot run
    // without it. The section loads it with `defer` ahead of this file, which
    // keeps the order — a missing helper is a build fault, and the tile fails
    // closed rather than adding to a cart it never cleared.
    var attrs = global.TwcCartAttributes;
    if (!attrs) {
      self._say(self._t('errorGeneric'), true);
      return;
    }

    self._busy(true);
    self._say(self._t('pendingText'), false);

    var root = routeRoot();

    // The three checkout attributes a JOIN owns — same rule as the sign-up
    // page (assets/twc-signup.js). twc_referred_by carries the captured
    // referral code. date_of_birth carries the buyer's birth date as
    // YYYY-MM-DD when this page knows it. purchase_type goes out EMPTY, which
    // removes a gift marker an earlier gifting checkout left on the cart —
    // /cart/clear.js keeps the cart attributes. All three merge into ONE
    // object, never replace each other.
    var updateBody = {};
    var code = global.TwcReferral && global.TwcReferral.get ? global.TwcReferral.get() : null;
    if (code) attrs.merge(updateBody, { twc_referred_by: code });
    attrs.merge(updateBody, { date_of_birth: attrs.dobIso(self), purchase_type: '' });

    var item = { id: variantId, quantity: 1 };
    if (sellingPlanId && !isNaN(sellingPlanId)) item.selling_plan = sellingPlanId;

    // FATAL at every step (see header): the clear is what guarantees the
    // visitor checks out with the chosen membership and nothing else.
    // After the add, and only after it: twc:add_to_cart from the add.js
    // body, then the ONE redirect once the bridge has settled (bounded at
    // ~300 ms). trackAdded never rejects, so a successful cart write always
    // reaches checkout; a failed step above skips it, so an error path never
    // publishes.
    attrs
      .clearThenAdd(root, updateBody, [item])
      .then(function (body) {
        return attrs.trackAdded('signup', attrs.addedItems(body));
      })
      .then(function () {
        global.location.href = '/checkout';
        // keep the pending state during navigation; pageshow releases it if the
        // visitor comes straight back.
      })
      .catch(function (err) {
        self._busy(false);
        var detail = err && err.data && (err.data.description || err.data.message);
        self._say(typeof detail === 'string' && detail !== '' ? detail : self._t('errorGeneric'), true);
      });
  };

  global.customElements.define('twc-brand-plans', Plans);
})(window);
